import { z } from 'zod';
import { isoDateTimeSchema, idSchema, slugSchema, timezoneSchema, } from './common.schema';
import { normalizeOrgDomain } from '../utils/org-domain';
/**
 * Tenant configuration. Document id IS the tenantId, so tenant docs do not
 * carry a separate `tenantId` field. Plan is set manually by an admin at
 * MVP (tech-arch §11.2); the Proteus entitlement read contract is P1.
 */
export const TENANT_PLANS = ['licensed', 'bundled', 'msp_premium'];
export const TENANT_STATUSES = ['active', 'suspended'];
export const tenantBrandingSchema = z
    .object({
    logoUrl: z.string().url().optional(),
    primaryColor: z
        .string()
        .regex(/^#[0-9a-fA-F]{6}$/, 'must be a #rrggbb hex color')
        .optional(),
    emailFromName: z.string().min(1).max(120).optional(),
})
    .strict();
/** Sign-in providers a tenant can require (the Firebase `sign_in_provider` claim values). */
export const SSO_SIGN_IN_PROVIDERS = { google: 'google.com', microsoft: 'microsoft.com' };
/**
 * Tenant sign-in policy (#9; mirrors Desk's TenantSecuritySettings). `ssoProviders` says
 * which SSO providers the tenant allows; `localPasswordDisabled` makes them the ONLY way in
 * (a fail-closed allowlist, enforced on every tenant-scoped request) and is only settable
 * when at least one provider is enabled. 2FA is whatever the IdP enforces — Detect has no
 * native MFA.
 */
export const tenantSecuritySettingsSchema = z
    .object({
    ssoProviders: z.object({ google: z.boolean(), microsoft: z.boolean() }).strict(),
    localPasswordDisabled: z.boolean(),
})
    .strict()
    .superRefine((val, ctx) => {
    if (val.localPasswordDisabled && !val.ssoProviders.google && !val.ssoProviders.microsoft) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['localPasswordDisabled'],
            message: 'localPasswordDisabled requires at least one SSO provider to be enabled',
        });
    }
});
/**
 * Tenant AI settings (#153, rung 3 AI option). Everything is OFF by default and opt-in: no AI call is made for a tenant
 * whose flag is off. `changeIntentAnalysis` lets Detect send a change window's title, purpose, success text and a compact
 * before/after summary of its in-scope resources to Gemini on Vertex AI in Detect's own GCP project, to judge whether
 * the change achieved its intent. Set by an account_admin only.
 */
export const tenantAiSettingsSchema = z.object({ changeIntentAnalysis: z.boolean() }).strict();
export const DEFAULT_TENANT_AI = { changeIntentAnalysis: false };
/** Documents written before the policy existed behave as "no restriction". */
export const DEFAULT_TENANT_SECURITY = {
    ssoProviders: { google: false, microsoft: false },
    localPasswordDisabled: false,
};
/**
 * The organization's canonical DNS domain — the Platform Directory key
 * (meshtek-core; auth#5). Stored in already-normalized form so the value
 * Detect registers is byte-identical to the directory's claim id.
 * Self-serve signup DERIVES this from the creator's provider-verified
 * email domain; it is never customer-typed free text, because a domain
 * claim pre-associates a customer across products (see org-domain.ts).
 * Operators may set it later for organizations that signed up from a
 * consumer mailbox — a superadmin-only field on update.
 */
export const orgDomainSchema = z
    .string()
    .min(1)
    .max(253)
    .refine((d) => normalizeOrgDomain(d) === d, {
    message: 'must be a canonical lowercase hostname (e.g. acme.com)',
});
export const tenantInputSchema = z
    .object({
    name: z.string().min(1).max(200),
    slug: slugSchema,
    plan: z.enum(TENANT_PLANS),
    contactEmail: z.string().email(),
    branding: tenantBrandingSchema.optional(),
    /** IANA timezone used for business-hours evaluation defaults. */
    timezone: timezoneSchema,
    /** Platform Directory key; null when none is known. */
    orgDomain: orgDomainSchema.nullable().optional(),
})
    .strict();
/**
 * Self-serve signup (desk-integration.md §9): any authenticated
 * interactive user creates their organization; the wizard's
 * Desk/Dispatch question becomes optional linkage blocks. Plan is NOT
 * caller-chosen — the API defaults it and platform operators adjust
 * (plan/status stay superadmin-only, tech-arch §11.2).
 */
export const tenantSignupInputSchema = z
    .object({
    name: z.string().min(1).max(200),
    slug: slugSchema,
    contactEmail: z.string().email(),
    timezone: timezoneSchema,
    /** Present iff the customer said "existing Desk customer". */
    desk: z
        .object({ deskTenantId: z.string().min(1).max(128) })
        .strict()
        .optional(),
    /** Present iff the customer said "existing Dispatch customer". */
    dispatch: z
        .object({ serviceProviderId: z.string().min(1).max(128) })
        .strict()
        .optional(),
})
    .strict();
/** Updates may clear nullable-at-rest fields with an explicit null. */
export const tenantUpdateSchema = tenantInputSchema
    .partial()
    .extend({
    branding: tenantBrandingSchema.nullable().optional(),
    /** Replaced as a whole (send both providers and the flag). account_admin only (the route's role). */
    security: tenantSecuritySettingsSchema.optional(),
    /** Replaced as a whole. account_admin only (the route's role). */
    ai: tenantAiSettingsSchema.optional(),
})
    .strict();
/** Stored shape: clearable optional fields are materialized as null. */
export const tenantSchema = tenantInputSchema
    .extend({
    branding: tenantBrandingSchema.nullable(),
    // Materialized at rest; absent on docs written before the Platform
    // Directory landed, so it defaults rather than failing their parse.
    orgDomain: orgDomainSchema.nullable().default(null),
    security: tenantSecuritySettingsSchema.default(DEFAULT_TENANT_SECURITY),
    // Absent on docs written before the AI option: OFF.
    ai: tenantAiSettingsSchema.default(DEFAULT_TENANT_AI),
    status: z.enum(TENANT_STATUSES),
    createdAt: isoDateTimeSchema,
    createdBy: idSchema,
    updatedAt: isoDateTimeSchema,
    lastModifiedBy: idSchema,
    deletedAt: isoDateTimeSchema.nullable(),
    deletedBy: idSchema.nullable(),
})
    .strict();
//# sourceMappingURL=tenant.schema.js.map